import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Locale = "fr" | "en";
export const LOCALES: Locale[] = ["fr", "en"];

type Dict = Record<string, string>;
type Translations = Record<Locale, Dict>;

// Minimal but coherent FR/EN dictionary for UI. Content (articles, fact-checks)
// uses its own multilingual fields.
const T: Translations = {
  fr: {
    "brand.tagline": "Information vérifiée. Sources protégées.",
    "nav.home": "Accueil",
    "nav.live": "Direct",
    "nav.factcheck": "Vérification",
    "nav.explainers": "Éducation aux médias",
    "nav.categories": "Catégories",
    "nav.rubriques": "Rubriques",
    "nav.about": "À propos",
    "nav.editorial": "Charte éditoriale",
    "nav.contact": "Contact",
    "nav.languages": "Langues",
    "nav.search": "Rechercher",
    "nav.profile": "Suivre",
    "nav.verify": "Vérifier",
    "cta.followAlerts": "Suivre les alertes",
    "cta.subscribe": "S'abonner",
    "verificationRequest.cta": "Demande de vérification",
    "verificationRequest.title": "Soumettre un contenu à vérifier",
    "verificationRequest.description": "Vous avez vu une information douteuse ? Décrivez-la ci-dessous, avec une pièce jointe si vous en avez une. Aucun compte n'est nécessaire.",
    "verificationRequest.textLabel": "Que souhaitez-vous nous signaler ?",
    "verificationRequest.textPlaceholder": "Décrivez le contenu, où vous l'avez vu, et pourquoi il vous semble douteux…",
    "verificationRequest.attachments": "Pièce jointe (facultatif)",
    "verificationRequest.addAttachment": "Ajouter une image, un audio ou une vidéo",
    "verificationRequest.attachmentHint": "20 Mo maximum par fichier.",
    "verificationRequest.wantsContact": "Je souhaite être recontacté(e) au sujet de ce signalement",
    "verificationRequest.name": "Nom",
    "verificationRequest.email": "Email",
    "verificationRequest.phone": "Téléphone",
    "verificationRequest.submit": "Envoyer",
    "verificationRequest.textRequired": "Merci de décrire le contenu à vérifier.",
    "verificationRequest.contactRequired": "Merci d'indiquer au moins un moyen de vous recontacter.",
    "verificationRequest.success": "Votre signalement a bien été envoyé, merci.",
    "verificationRequest.submitError": "Échec de l'envoi, veuillez réessayer.",
    "verificationRequest.uploadError": "Échec de l'envoi du fichier.",
    "cta.share": "Partager",
    "cta.readMore": "Lire la suite",
    "cta.listen": "Écouter",
    "cta.followLive": "Suivre le direct",
    "alert.banner": "Alerte vérifiée",
    "section.latest": "Dernières informations",
    "section.factcheck": "Vérifications & fact-checking",
    "section.live": "En direct",
    "section.explainers": "Décryptages",
    "section.in60": "En 60 secondes",
    "section.in60.desc": "Bulletins audio et vidéo présentés par nos présentateurs numériques.",
    "section.follow": "Recevoir les alertes",
    "section.follow.desc": "Information vérifiée, sans spam.",
    "section.related": "À lire également",

    // ── Commentaires ────────────────────────────────────────────────────────
    "comments.title": "Commentaires",
    "comments.likes": "J'aime",
    "comments.empty": "Aucun commentaire pour le moment.",
    "comments.form.name": "Votre nom",
    "comments.form.placeholder": "Votre commentaire…",
    "comments.form.submit": "Publier le commentaire",
    "comments.form.required": "Nom et commentaire sont requis.",
    "comments.form.error": "Impossible d'envoyer le commentaire.",
    "comments.form.sent": "Commentaire publié, merci !",
    "label.byDesk": "Rédaction Ramba News",
    "label.partnerDesk": "Rédaction Partenaire",
    "label.readingTime": "min de lecture",
    "label.published": "Publié",
    "label.updated": "Mis à jour",
    "label.tags": "Mots-clés",
    "label.languages": "Disponible en",
    "label.aiPresenter": "Présentateur numérique IA",
    "label.audioOnly": "Audio seulement",
    "label.lowData": "Mode faible consommation",
    "label.sourceProtected": "Sources protégées",
    "label.correction": "Correction",
    "verdict.false": "Faux",
    "verdict.misleading": "Trompeur",
    "verdict.unproven": "Non prouvé",
    "verdict.true": "Vrai",
    "verdict.context": "Manque de contexte",
    "priority.confirmed": "Confirmé",
    "priority.developing": "En développement",
    "priority.correction": "Correction",
    "priority.alert": "Alerte",
    "footer.about": "À propos",
    "footer.editorial": "Charte éditoriale",
    "footer.contact": "Contact",
    "footer.privacy": "Confidentialité",
    "footer.terms": "Conditions",
    "cookies.title": "Gestion des cookies",
    "cookies.description": "Nous utilisons des cookies pour mesurer l'audience et améliorer votre expérience sur Ramba News.",
    "cookies.learnMore": "En savoir plus",
    "cookies.accept": "Accepter",
    "cookies.reject": "Refuser",
    "footer.languages": "Langues",
    "footer.copy": "© {year} Ramba News — Plateforme média numérique sécurisée.",
    "search.placeholder": "Rechercher des articles, vérifications, directs…",
    "countryBar.all": "Tous les pays",
    "filter.all": "Tout",
    "filter.category": "Catégorie",
    "filter.date": "Date",
    "filter.type": "Type",
    "filter.contentType": "Type de contenu",
    "filter.verdict": "Verdict",
    "filter.language": "Langue",
    "form.email": "Adresse e-mail",
    "form.password": "Mot de passe",
    "form.submit": "Connexion",
    "form.message": "Message",
    "form.name": "Nom (optionnel)",
    "form.confidentialTip": "Information confidentielle",
    "admin.title": "Administration éditoriale",
    "admin.signin": "Connexion sécurisée",
    "admin.noPublic": "L'accès est réservé aux membres autorisés.",
    "admin.accessCode": "Code d'accès",
    "admin.connecting": "Connexion…",
    "admin.sessionSecurity": "Session chiffrée. Toute tentative est journalisée.",
    "admin.authError": "Email ou mot de passe incorrect.",
    "brand.newsroom": "Salle de presse numérique sécurisée",
    "footer.description": "Ramba News est une plateforme média numérique sécurisée dédiée à l'information vérifiée, au journalisme responsable et à la résilience du public face à la désinformation en Afrique de l'Ouest.",
    "footer.legal": "Légal",
    "footer.admin": "Espace éditorial",
    "form.email.placeholder": "Saisissez votre adresse e-mail",
    "contact.heading": "Nous contacter",
    "contact.description": "Pour toute question éditoriale ou tout signalement confidentiel, utilisez le formulaire sécurisé ci-dessous.",
    "contact.security": "Vos informations sont traitées de manière confidentielle. Aucune coordonnée personnelle de notre équipe n'est exposée publiquement pour des raisons de sécurité.",
    "contact.general": "Contact général",
    "contact.tips": "Signalements",
    "about.heading": "Une plateforme média numérique sécurisée",
    "about.intro": "Ramba News est une plateforme média numérique sécurisée, en ligne et distribuée, dédiée à l'information vérifiée, au journalisme responsable et à la résilience du public face à la désinformation. Son modèle éditorial est conçu pour protéger les sources, les contributeurs et les partenaires, tout en offrant au public une information fiable et accessible.",
    "about.card.verified.title": "Information vérifiée",
    "about.card.verified.body": "Recoupement systématique de plusieurs sources indépendantes avant publication.",
    "about.card.sources.title": "Sources protégées",
    "about.card.sources.body": "Workflows éditoriaux sécurisés et protection rigoureuse des contributeurs.",
    "about.card.multilingual.title": "Multilingue",
    "about.card.multilingual.body": "Français, anglais, et versions en langues locales lorsque cela renforce l'accès.",
    "about.approach.title": "Notre approche",
    "about.approach.body": "Nous fonctionnons comme une rédaction numérique sécurisée et distribuée. Nos workflows protègent les contributeurs et les partenaires, et nos présentateurs numériques (avatars IA) sont identifiés de manière transparente comme tels — jamais comme des journalistes humains.",
    "editorial.heading": "Nos principes",
    "editorial.description": "Ces principes guident notre travail quotidien et structurent notre processus éditorial.",
    "editorial.accuracy.title": "Exactitude",
    "editorial.accuracy.body": "Nous publions uniquement ce que nous avons vérifié. En cas d'incertitude, nous le disons.",
    "editorial.verification.title": "Vérification",
    "editorial.verification.body": "Recoupement systématique de plusieurs sources indépendantes avant publication.",
    "editorial.independence.title": "Indépendance",
    "editorial.independence.body": "Nos décisions éditoriales sont prises sans pression extérieure.",
    "editorial.neutrality.title": "Neutralité",
    "editorial.neutrality.body": "Nous présentons les faits avec rigueur, sans positionnement militant.",
    "editorial.sources.title": "Protection des sources",
    "editorial.sources.body": "Les contributeurs et sources sensibles sont protégés selon des protocoles sécurisés.",
    "editorial.nohate.title": "Pas de discours haineux",
    "editorial.nohate.body": "Nous refusons toute publication incitant à la haine, à la discrimination ou à la violence.",
    "editorial.nopropaganda.title": "Pas de propagande",
    "editorial.nopropaganda.body": "Notre rôle est d'informer, pas de relayer des récits orientés.",
    "editorial.ai.title": "Usage responsable de l'IA",
    "editorial.ai.body": "Nos présentateurs numériques sont étiquetés comme tels et soumis à validation humaine avant publication.",
    "editorial.correction.title": "Politique de correction",
    "editorial.correction.body": "Toute erreur identifiée est corrigée publiquement avec une note datée.",
    "languages.heading": "Une information accessible",
    "languages.description": "Ramba News publie en français et en anglais, avec des versions disponibles en bambara, haoussa et mooré pour élargir l'accès à une information vérifiée et compréhensible.",
    "languages.content": "Articles, audio et vidéo",
    "languages.note": "L'interface principale du site est disponible en français et en anglais. Le contenu peut comporter des versions traduites supplémentaires selon les sujets et les besoins du public.",
    "error.404.title": "Page introuvable",
    "error.404.desc": "La page demandée n'existe pas ou a été déplacée.",
    "error.404.cta": "Retour à l'accueil",
    "error.load": "Impossible de charger le contenu.",
    "cta.retry": "Réessayer",
    "home.empty.articles": "Aucun article publié pour l'instant.",
    "home.empty.verifications": "Aucune vérification publiée pour l'instant.",
    "home.empty.live": "Aucun direct en cours.",
    "common.loading": "Chargement…",
    "cta.copy": "Copier",

    // ── Direct public ─────────────────────────────────────────────────────────
    "live.badge": "En direct",
    "live.updated": "Suivi mis à jour à chaque vérification",
    "live.intro": "Mises à jour chronologiques. Aucune information non vérifiée. Les corrections sont signalées explicitement.",
    "live.updatesCount": "{count} mises à jour",
    "live.pinned": "Épinglé",

    // ── Article public ────────────────────────────────────────────────────────
    "article.backHome": "← Retour à l'accueil",
    "article.verified": "Vérifié",
    "article.aiAssisted.badge": "Contenu assisté par IA",
    "article.aiAssisted.dialogTitle": "Utilisation de l'IA dans cet article",
    "article.aiAssisted.charte": "Cet article a été rédigé avec l'assistance d'un outil d'intelligence artificielle.\n\nChez Ramba News, l'IA peut aider à la recherche, à la mise en forme ou à la traduction, mais ne remplace jamais la vérification humaine : chaque fait, chaque citation et chaque source sont validés par notre rédaction avant publication.\n\nLa responsabilité éditoriale de cet article reste entièrement humaine.",
    "article.listen": "Écouter l'article",
    "article.listening": "Lecture en cours…",
    "article.copied": "Copié !",
    "article.copyLink": "Copier le lien",
    "article.correctionNote": "Une donnée a été ajustée après nouvelle vérification.",
    "article.fallback.p1": "Notre rédaction applique systématiquement un protocole de vérification : recoupement de plusieurs sources indépendantes, analyse des documents publics disponibles et contrôle éditorial avant publication.",
    "article.fallback.p2": "Les éléments présentés dans cet article ont été vérifiés au moment de la publication. Toute évolution significative sera signalée par une mise à jour datée.",

    // ── Catégorie public ──────────────────────────────────────────────────────
    "category.eyebrow": "Catégorie",
    "category.empty": "Aucun contenu publié pour cette catégorie pour le moment.",
    "rubrique.eyebrow": "Rubrique",
    "rubrique.sidebar.all": "Toutes les catégories",

    // ── Fact-check public ─────────────────────────────────────────────────────
    "factcheck.intro": "Chaque vérification suit une méthodologie publique : sources, méthode, preuves et version partage sécurisé.",
    "factcheck.loadError": "Impossible de charger les vérifications.",
    "factcheck.emptyFilter": "Aucune vérification pour ce filtre.",
    "factcheck.notFound": "Vérification introuvable ou erreur de chargement.",
    "factcheck.backAll": "← Toutes les vérifications",
    "factcheck.section.circulating": "Ce qui circule",
    "factcheck.section.verified": "Ce que nous avons vérifié",
    "factcheck.section.evidence": "Preuves & méthode",
    "factcheck.section.why": "Pourquoi cela compte",
    "factcheck.attachments.title": "Pièces jointes",
    "factcheck.attachments.document": "Ouvrir le document",
    "factcheck.safeShareTitle": "Version partage sécurisé",
    "factcheck.filter.context": "Contexte",

    // ── Recherche public ──────────────────────────────────────────────────────
    "search.filter.24h": "24h",
    "search.filter.7days": "7 jours",
    "search.filter.30days": "30 jours",
    "search.articles": "Articles",
    "search.verifications": "Vérifications",

    // ── Confidentialité ───────────────────────────────────────────────────────
    "privacy.heading": "Confidentialité",
    "privacy.body": "Ramba News applique une politique stricte de protection des données. Nous ne collectons que les informations strictement nécessaires au fonctionnement du service et ne partageons aucune donnée identifiante avec des tiers à des fins commerciales.",

    // ── Conditions d'utilisation ──────────────────────────────────────────────
    "terms.heading": "Conditions d'utilisation",
    "terms.body": "L'utilisation de cette plateforme implique l'acceptation des présentes conditions. Le contenu publié est protégé par les lois en vigueur sur la propriété intellectuelle. Toute reproduction doit citer Ramba News comme source.",

    // ── Récap hebdomadaire ────────────────────────────────────────────────────
    "recap.heading": "Le récap de la semaine",
    "recap.subtitle": "Tous les articles publiés ces 7 derniers jours, en un coup d'œil.",
    "recap.empty": "Aucun article publié cette semaine pour le moment.",

    // ── Désinscription newsletter ─────────────────────────────────────────────
    "unsubscribe.heading": "Désinscription de la newsletter",
    "unsubscribe.intro": "Vous ne recevrez plus les emails de la newsletter Ramba News à l'adresse suivante :",
    "unsubscribe.emailLabel": "Adresse email",
    "unsubscribe.emailPlaceholder": "vous@exemple.com",
    "unsubscribe.confirm": "Se désinscrire",
    "unsubscribe.success": "Vous avez bien été désinscrit·e. Vous ne recevrez plus nos emails.",
    "unsubscribe.error": "Impossible de vous désinscrire pour le moment. Réessayez plus tard.",
    "unsubscribe.notFound": "Cette adresse email n'est pas inscrite à la newsletter.",

    // ── Hero ──────────────────────────────────────────────────────────────────
    "hero.noArticle": "Aucun article disponible",
    "hero.firstArticleHint": "Le premier article publié apparaîtra ici.",
    "hero.verified": "Vérifié",
    "hero.noTitle": "Sans titre",

    // ── Statuts éditoriaux ────────────────────────────────────────────────────
    "status.published": "Publié",
    "status.draft": "Brouillon",
    "status.inReview": "En révision",
    "status.pending": "En attente",
    "status.scheduled": "Programmé",
    "status.archived": "Archivé",
    "status.rejected": "Rejeté",
    "status.rendered": "Rendu",
    "status.ready": "Prêt",

    // ── Admin Analytics ───────────────────────────────────────────────────────
    "admin.analytics.title": "Analytics",
    "admin.analytics.subtitle": "Audience et engagement ",
    "admin.analytics.stat.views24h": "Vues 24h",
    "admin.analytics.stat.shares": "Partages",
    "admin.analytics.stat.activeLangs": "Langues actives",
    "admin.analytics.stat.lowData": "Mode faible conso.",
    "admin.analytics.chart.title": "Évolution (14 jours)",
    "admin.analytics.chart.desc": "Graphique placeholder",
    "admin.analytics.topContent": "Top contenus",
    "admin.analytics.col.views": "Vues",

    // ── Admin Direct ──────────────────────────────────────────────────────────
    "admin.live.title": "Direct",
    "admin.live.subtitle": "Création et gestion des suivis en direct",
    "admin.live.openFeed": "Suivi ouvert",
    "admin.live.close": "Clôturer",
    "admin.live.newUpdate": "Nouvelle mise à jour vérifiée…",

    // ── Admin Sécurité ────────────────────────────────────────────────────────
    "admin.security.title": "Revue de sécurité",
    "admin.security.subtitle": "Validation obligatoire avant publication sensible",
    "admin.security.checklist": "Checklist de sécurité éditoriale",
    "admin.security.checklistDesc": "Chaque case doit être traitée par un relecteur sécurité",
    "admin.security.check.1": "Ce contenu expose-t-il une source ?",
    "admin.security.check.2": "Ce contenu révèle-t-il une localisation ?",
    "admin.security.check.3": "Ce contenu contient-il des risques de métadonnées ?",
    "admin.security.check.4": "Ce contenu cite-t-il des noms inutilement ?",
    "admin.security.check.5": "Ce contenu crée-t-il un risque physique, numérique ou légal ?",
    "admin.security.reviewerNote": "Note du relecteur : —",
    "admin.security.approve": "Approuver la publication",
    "admin.security.block": "Bloquer",

    // ── Admin Sources ─────────────────────────────────────────────────────────
    "admin.sources.title": "Sources & partenaires",
    "admin.sources.subtitle": "Zone confidentielle — accès restreint",
    "admin.sources.alertTitle": "Données strictement confidentielles",
    "admin.sources.alertBody": "Aucune information de cette section ne doit jamais apparaître sur le site public ou être partagée hors workflow sécurisé.",
    "admin.sources.network": "Réseau protégé",
    "admin.sources.col.code": "Code",
    "admin.sources.col.trust": "Niveau de confiance",
    "admin.sources.col.channel": "Canal",
    "admin.sources.col.risk": "Risque",
    "admin.sources.col.history": "Historique",

    // ── Admin Avatars ─────────────────────────────────────────────────────────
    "admin.avatars.title": "Production avatars",
    "admin.avatars.subtitle": "Présentateurs numériques IA — validation humaine requise",
    "admin.avatars.queue": "File de production",
    "admin.avatars.queueDesc": "Validation humaine obligatoire avant publication",
    "admin.avatars.col.content": "Contenu",
    "admin.avatars.col.language": "Langue",
    "admin.avatars.col.voice": "Voix",
    "admin.avatars.col.audioOnly": "Audio seul",
    "admin.avatars.yes": "Oui",
    "admin.avatars.no": "Non",

    // ── Admin retour site ─────────────────────────────────────────────────────
    "admin.backToSite": "← Site public",

    // ── Admin navigation ──────────────────────────────────────────────────────
    "admin.nav.open": "Ouvrir le menu",
    "admin.nav.dashboard": "Tableau de bord",
    "admin.nav.workflow": "Workflow éditorial",
    "admin.nav.content": "Contenus",
    "admin.nav.articles": "Articles",
    "admin.article.viewAll": "Voir tous les articles",
    "admin.nav.factchecks": "Vérifications",
    "admin.nav.analytics": "Statistiques",
    "admin.nav.translations": "Traductions",
    "admin.nav.roles": "Utilisateurs & rôles",
    "admin.nav.disinformation": "Désinformation",
    "admin.nav.settings": "Paramètres",
    "admin.logout": "Se déconnecter",
    "admin.secureSession": "Session sécurisée",

    // ── Admin commun ──────────────────────────────────────────────────────────
    "admin.common.cancel": "Annuler",
    "admin.common.save": "Enregistrer",
    "admin.common.saved": "Enregistré",
    "admin.common.approve": "Approuver",
    "admin.common.reject": "Rejeter",
    "admin.common.delete": "Supprimer",
    "admin.common.edit": "Modifier",
    "admin.common.add": "Ajouter",
    "admin.common.next": "Suivant",
    "admin.common.back": "Retour",
    "admin.common.skip": "Ignorer",
    "admin.common.new": "Nouveau",
    "admin.common.search": "Rechercher…",
    "admin.common.loading": "Chargement…",
    "admin.common.publish": "Publier",
    "admin.common.page": "Page {page} / {total}",
    "admin.common.noResult": "Aucun résultat",
    "admin.common.translated": "Traduit",
    "admin.common.missing": "Manquant",
    "admin.common.metadata": "Métadonnées",
    "admin.common.translations": "Traductions",
    "admin.common.multilingual": "Complétion multilingue",
    "admin.common.col.title": "Titre",
    "admin.common.col.type": "Type",
    "admin.common.col.status": "Statut",
    "admin.common.col.updated": "Mis à jour",
    "admin.common.col.date": "Date",
    "admin.common.col.actions": "Actions",
    "admin.common.col.languages": "Langues",
    "admin.common.col.verdict": "Verdict",
    "admin.common.col.format": "Format",
    "admin.common.col.claim": "Affirmation",
    "admin.common.langues": "{count} / {total} langues traduites — {pct}% de complétion",
    "admin.common.upload": "Envoi en cours…",
    "admin.common.sendToS3": "Envoi vers S3…",
    "admin.common.saveError": "Erreur lors de l'enregistrement.",
    "admin.common.deleteError": "Erreur lors de la suppression.",
    "admin.common.uploadError": "Erreur lors de l'upload.",

    "admin.users.title": "Utilisateurs & rôles",
    "admin.users.subtitle": "Inviter des utilisateurs et gérer leurs droits d'accès",
    "admin.users.invite.title": "Inviter un utilisateur",
    "admin.users.invite.emailPlaceholder": "prenom.nom@example.com",
    "admin.users.invite.submit": "Inviter",
    "admin.users.role.admin": "Admin",
    "admin.users.role.redacteur": "Rédacteur",
    "admin.users.role.partenaire": "Partenaire",
    "admin.users.col.email": "Email",
    "admin.users.col.role": "Rôle",
    "admin.users.col.expires": "Expire le",
    "admin.users.col.nom": "Nom",
    "admin.users.col.roles": "Rôles",
    "admin.users.col.code": "Code",
    "admin.users.invitations.title": "Invitations ({count})",
    "admin.users.invitations.empty": "Aucune invitation envoyée",
    "admin.users.invitations.status.pending": "En attente",
    "admin.users.invitations.status.accepted": "Acceptée",
    "admin.users.invitations.status.expired": "Expirée",
    "admin.users.invitations.status.cancelled": "Annulée",
    "admin.users.invitations.cancel": "Annuler",
    "admin.users.list.title": "Utilisateurs ({count})",
    "admin.users.status.blocked": "Bloqué",
    "admin.users.status.active": "Actif",
    "admin.users.status.attempts": "{count} tentatives",
    "admin.users.lock.label": "Verrouillé {minutes} min",
    "admin.users.lock.tooltip": "Verrou anti-bruteforce temporaire (indépendant du statut ci-dessus) : la connexion échoue encore même si le compte est actif.",
    "admin.users.lock.action": "Lever le verrou",
    "admin.users.action.block": "Bloquer",
    "admin.users.action.unblock": "Débloquer",
    "admin.users.deleteConfirm.title": "Supprimer cet utilisateur ?",
    "admin.users.deleteConfirm.description": "« {email} » sera définitivement supprimé.",
    "admin.users.unblockDialog.title": "Débloquer {email}",
    "admin.users.unblockDialog.description": "Ce message sera envoyé par email à l'utilisateur pour l'informer du déblocage de son compte.",
    "admin.users.unblockDialog.placeholder": "Ex. : Votre compte a été débloqué, merci de réessayer de vous connecter…",
    "admin.users.unblockDialog.submit": "Débloquer et envoyer",
    "admin.users.toast.actionError": "Action impossible",
    "admin.users.toast.blocked": "{email} bloqué",
    "admin.users.toast.lockLifted": "Verrou temporaire levé pour {email}",
    "admin.users.toast.unblocked": "{email} débloqué, un email lui a été envoyé",
    "admin.users.toast.invitationCancelled": "Invitation à {email} annulée",
    "admin.users.toast.invitationCancelError": "Impossible d'annuler cette invitation",
    "admin.users.toast.deleted": "{email} supprimé",
    "admin.users.toast.deleteError": "Impossible de supprimer cet utilisateur",
    "admin.users.toast.invitationSent": "Invitation envoyée à {email}",
    "admin.users.toast.inviteError": "Impossible d'envoyer l'invitation",

    // ── Tableau de bord ───────────────────────────────────────────────────────
    "admin.dashboard.subtitle": "Vue d'ensemble de la rédaction sécurisée",
    "admin.dashboard.publishedToday": "Publiés aujourd'hui",
    "admin.dashboard.publishedToday.hint": "+2 vs hier",
    "admin.dashboard.pendingReview": "En attente de revue",
    "admin.dashboard.pendingReview.hint": "3 en vérification",
    "admin.dashboard.verif": "Vérifications en cours",
    "admin.dashboard.verifiedAlerts": "Alertes vérifiées",
    "admin.dashboard.recentActivity": "Activité éditoriale récente",
    "admin.dashboard.recentActivity.desc": "Derniers contenus en cours dans le workflow",
    "admin.dashboard.security": "Sécurité",
    "admin.dashboard.security.desc": "Aucun incident actif",
    "admin.dashboard.security.nominal": "Tout est nominal",
    "admin.dashboard.security.audit": "Dernier audit : il y a 2 h",
    "admin.dashboard.live": "Direct",
    "admin.dashboard.live.desc": "Suivi en cours",
    "admin.dashboard.live.open": "1 direct ouvert",
    "admin.dashboard.live.updates": "5 mises à jour vérifiées",
    "admin.dashboard.audience": "Audience",
    "admin.dashboard.audience.desc": "Depuis le début",
    "admin.dashboard.audience.views": "Vues",
    "admin.dashboard.audience.reads": "Lectures",
    "admin.dashboard.audience.shares": "Partages",
    "admin.dashboard.audience.visitors": "Visiteurs uniques",
    "admin.dashboard.pendingVerif": "Vérifications en attente",
    "admin.dashboard.col.proposedVerdict": "Verdict proposé",
    "admin.dashboard.col.validatedBy": "Validé par",
    "admin.dashboard.noTranslation": "Sans traduction",

    // ── Workflow ──────────────────────────────────────────────────────────────
    "admin.workflow.subtitle": "Pipeline de validation et de sécurité",
    "admin.workflow.steps": "Étapes du workflow",
    "admin.workflow.pipeline": "Contenus dans le pipeline",
    "admin.workflow.col.step": "Étape",
    "admin.workflow.col.assigned": "Assigné à",
    "admin.workflow.state.draft": "Brouillon",
    "admin.workflow.state.submitted": "Soumis",
    "admin.workflow.state.verifying": "En vérification",
    "admin.workflow.state.security": "Revue sécurité",
    "admin.workflow.state.translation": "Prêt traduction",
    "admin.workflow.state.avatar": "Prêt avatar",
    "admin.workflow.state.scheduled": "Programmé",
    "admin.workflow.state.published": "Publié",
    "admin.workflow.state.corrected": "Corrigé",
    "admin.workflow.state.archived": "Archivé",
    "admin.workflow.team.editorial": "Rédaction",
    "admin.workflow.team.verif": "Vérif.",
    "admin.workflow.team.security": "Sécurité",
    "admin.workflow.team.translation": "Trad.",

    // ── Rôles & permissions ───────────────────────────────────────────────────
    "admin.roles.subtitle": "Matrice d'accès",
    "admin.roles.matrix": "Matrice de permissions",
    "admin.roles.col.role": "Rôle",
    "admin.roles.superAdmin": "Super Admin",
    "admin.roles.editor": "Rédacteur en chef",
    "admin.roles.writer": "Rédacteur",
    "admin.roles.verifier": "Vérificateur",
    "admin.roles.translator": "Traducteur",
    "admin.roles.avatarProd": "Producteur avatar",
    "admin.roles.securityReviewer": "Relecteur sécurité",
    "admin.roles.analyst": "Analyste",
    "admin.roles.reader": "Lecteur",
    "admin.roles.perm.read": "Lire",
    "admin.roles.perm.edit": "Éditer",
    "admin.roles.perm.publish": "Publier",
    "admin.roles.perm.delete": "Supprimer",
    "admin.roles.perm.security": "Sécurité",
    "admin.roles.perm.admin": "Admin",

    // ── Désinformation ────────────────────────────────────────────────────────
    "admin.disinfo.title": "Surveillance désinformation",
    "admin.disinfo.subtitle": "Récits viraux et niveau de risque",
    "admin.disinfo.tracked": "Récits suivis",
    "admin.disinfo.response": "Réponse",

    // ── Paramètres ────────────────────────────────────────────────────────────
    "admin.settings.subtitle": "Configuration de la plateforme",
    "admin.settings.configurable": "Section configurable. Connecter au backend pour activer.",
    "admin.settings.identity.title": "Identité du site",
    "admin.settings.identity.desc": "Nom, description, logo, favicon",
    "admin.settings.languages.title": "Langues",
    "admin.settings.languages.desc": "FR, EN, BM, HA, MOS — visibilité publique configurable",
    "admin.settings.categories.title": "Catégories",
    "admin.settings.categories.desc": "Gestion des catégories éditoriales",

    // ── Page Catégories (admin) ────────────────────────────────────────────────
    "admin.nav.categories": "Catégories",
    "admin.categories.subtitle": "Créer, modifier ou supprimer les catégories éditoriales",
    "admin.categories.form.title": "Ajouter / modifier une catégorie",
    "admin.categories.form.slugPlaceholder": "ex : gouvernance",
    "admin.categories.form.labelFr": "Libellé FR",
    "admin.categories.form.labelEn": "Libellé EN",
    "admin.categories.form.rubrique": "Rubrique",
    "admin.categories.form.rubriqueNone": "Aucune",
    "admin.categories.list.title": "Catégories disponibles ({count})",
    "admin.categories.list.empty": "Aucune catégorie pour le moment.",
    "admin.categories.deleteConfirm": "Supprimer cette catégorie ?",

    // ── Page Étiquettes (admin) ────────────────────────────────────────────────
    "admin.nav.etiquettes": "Étiquettes",
    "admin.etiquettes.subtitle": "Créer, modifier ou supprimer les étiquettes",
    "admin.etiquettes.form.title": "Ajouter / modifier une étiquette",
    "admin.etiquettes.form.slugPlaceholder": "ex : elections",
    "admin.etiquettes.list.title": "Étiquettes disponibles ({count})",
    "admin.etiquettes.list.empty": "Aucune étiquette pour le moment.",
    "admin.etiquettes.deleteConfirm.title": "Supprimer cette étiquette ?",
    "admin.etiquettes.deleteConfirm.desc": "Cette action est irréversible. Elle sera retirée des articles qui l'utilisent.",

    // ── Page Rubriques (admin) ─────────────────────────────────────────────────
    "admin.nav.rubriques": "Rubriques",
    "admin.rubriques.subtitle": "Créer, modifier ou supprimer les rubriques (regroupements de catégories)",
    "admin.rubriques.form.title": "Ajouter / modifier une rubrique",
    "admin.rubriques.form.slugPlaceholder": "ex : politique",
    "admin.rubriques.form.nom": "Nom",
    "admin.rubriques.form.nomEn": "Nom (EN)",
    "admin.rubriques.form.ordre": "Ordre",
    "admin.rubriques.form.actif": "Active",
    "admin.rubriques.list.title": "Rubriques disponibles ({count})",
    "admin.rubriques.list.empty": "Aucune rubrique pour le moment.",
    "admin.rubriques.inactiveBadge": "inactive",
    "admin.rubriques.deleteConfirm.title": "Supprimer cette rubrique ?",
    "admin.rubriques.deleteConfirm.desc": "Cette action est irréversible. Les catégories rattachées ne seront pas supprimées, mais perdront leur rattachement à cette rubrique.",
    "admin.translations.deleteConfirm.title": "Supprimer cette traduction ?",
    "admin.translations.deleteConfirm.desc": "Cette action est irréversible.",
    "admin.translations.split.hint": "Version française figée à gauche, à titre de référence pour la traduction",
    "admin.detail.article.media.deleteConfirm.title": "Supprimer ce média ?",
    "admin.detail.article.media.deleteConfirm.desc": "Cette action est irréversible.",
    "admin.detail.verif.attachments.deleteConfirm.title": "Supprimer cette pièce jointe ?",
    "admin.detail.verif.attachments.deleteConfirm.desc": "Cette action est irréversible.",

    // ── Session admin ──────────────────────────────────────────────────────────
    "admin.session.expiringSoon.title": "Votre session va bientôt expirer",
    "admin.session.expiringSoon.desc": "Vous serez déconnecté automatiquement dans",
    "admin.session.logoutNow": "Se déconnecter",
    "admin.session.extend": "Prolonger la session",

    "admin.settings.workflow.title": "Workflow",
    "admin.settings.workflow.desc": "Étapes de validation et règles d'approbation",
    "admin.settings.notifications.title": "Notifications",
    "admin.settings.notifications.desc": "Alertes email, WhatsApp, Telegram",
    "admin.settings.security.title": "Sécurité",
    "admin.settings.security.desc": "2FA, sessions, audit log, IP allowlist",
    "admin.settings.api.title": "API",
    "admin.settings.api.desc": "Clés et webhooks ",
    "admin.settings.cms.title": "CMS",
    "admin.settings.cms.desc": "Intégrations CMS ",

    // ── Articles ──────────────────────────────────────────────────────────────
    "admin.content.subtitle": "Création, édition et publication des articles",
    "admin.content.noArticle": "Aucun article trouvé",
    "admin.article.new": "Nouvel article",
    "admin.article.step.meta": "Métadonnées",
    "admin.article.step.translation": "Traduction FR",
    "admin.article.step.media": "Médias",
    "admin.article.rubrique": "Rubrique",
    "admin.article.selectRubrique": "Sélectionnez une rubrique.",
    "admin.article.categories": "Catégories",
    "admin.article.select": "Sélectionner…",
    "admin.article.signature": "Signature",
    "admin.article.tags": "Étiquettes",
    "admin.article.noTags": "Aucune étiquette disponible",
    "admin.article.titre": "Titre",
    "admin.article.sousTitre": "Sous-titre",
    "admin.article.corps": "Corps de l'article",
    "admin.article.commentsAllowed": "Autoriser les commentaires",
    "admin.article.aiAssisted": "Écrit avec l'assistance d'une IA",
    "admin.article.sponsored": "Article sponsorisé",
    "admin.article.new.title": "Ajouter un article",
    "admin.article.new.subtitle": "Créer un nouvel article — les médias et langues additionnelles se gèrent après création",
    "admin.article.new.meta": "Informations générales",
    "admin.article.new.publication": "Publication",
    "admin.article.schedule": "Programmer pour",
    "admin.article.dateCreation": "Date de création",
    "admin.article.dateModification": "Date de modification",
    "admin.article.datePublie": "Date de publication",
    "admin.article.publish": "Publier",
    "admin.article.schedulePublish": "Programmer",
    "admin.article.saveDraft": "Enregistrer en brouillon",
    "admin.article.auteur": "Auteur",
    "admin.article.auteurNone": "— Choisir —",
    "admin.article.coverImage": "Image à la une",
    "admin.article.chooseCover": "Choisir dans la médiathèque…",
    "admin.article.metaTitre": "Meta title",
    "admin.article.metaDescription": "Meta description",
    "admin.article.new.content": "Contenu (français)",
    "admin.editor.saving": "Enregistrement…",
    "admin.editor.saved": "Enregistré",
    "admin.article.readingTime": "Temps de lecture (en minute)",
    "admin.article.addMedia": "Ajouter un média",
    "admin.article.noMedia": "Aucun média ajouté",
    "admin.article.mediaOptional": "Cette étape est optionnelle. Vous pouvez ajouter des médias maintenant ou ignorer cette étape.",
    "admin.article.inProgress": "• En cours…",
    "admin.article.filesCount": "Fichiers ({count})",
    "admin.article.rubrique.label": "Catégorie :",
    "admin.article.type.label": "Type :",
    "admin.article.waitUpload": "Attendez la fin des uploads avant de créer.",
    "admin.article.selectCategory": "Sélectionnez une rubrique.",
    "admin.article.titleRequired": "Le titre en français est requis.",
    "admin.article.uploadError": "Erreur d'upload",
    "admin.article.creationError": "Erreur lors de la création.",
    "admin.article.delete.title": "Supprimer cet article ?",
    "admin.article.delete.desc": "sera supprimé définitivement, ainsi que toutes ses traductions.",
    "admin.article.trad.edit": "Modification de la traduction existante",
    "admin.article.trad.new": "Nouvelle traduction pour cet article",
    "admin.article.trad.titleRequired": "Le titre est requis.",

    // ── Vérifications ─────────────────────────────────────────────────────────
    "admin.factchecks.subtitle": "Éditeur fact-check et bibliothèque",
    "admin.factchecks.new": "Nouvelle",
    "admin.factchecks.all": "Tous",
    "admin.factchecks.empty": "Aucune vérification trouvée",
    "admin.factcheck.new": "Nouvelle vérification",
    "admin.factcheck.step.meta": "Métadonnées",
    "admin.factcheck.step.translation": "Traductions Fr",
    "admin.factcheck.verdict": "Verdict *",
    "admin.factcheck.format": "Format",
    "admin.factcheck.rubrique": "Rubrique",
    "admin.factcheck.none": "Aucune",
    "admin.factcheck.affirmationRequired": "L'affirmation en français est requise.",
    "admin.factcheck.creationError": "Erreur lors de la création.",
    "admin.factcheck.verdict.label": "Verdict :",
    "admin.factcheck.format.label": "Format :",
    "admin.factcheck.rubrique.label": "Rubrique :",
    "admin.factcheck.field.affirmation": "Affirmation *",
    "admin.factcheck.field.explication": "Explication courte",
    "admin.factcheck.field.ceQuiCircule": "Ce qui circule",
    "admin.factcheck.field.ceQueNousAvonsVerifie": "Ce que nous avons vérifié",
    "admin.factcheck.field.preuves": "Preuves",
    "admin.factcheck.field.pourquoiImportant": "Pourquoi c'est important",
    "admin.factcheck.field.partageSecurise": "Version partage sécurisé",
    "admin.factcheck.field.partageSecurise2": "Partage sécurisé",
    "admin.factcheck.delete.title": "Supprimer cette vérification ?",
    "admin.factcheck.delete.desc": "sera supprimée définitivement.",

    // ── Page Traductions ──────────────────────────────────────────────────────
    "admin.translations.subtitle": "Suivi et gestion des versions multilingues — articles et vérifications",
    "admin.translations.filter.articles": "Filtrer par titre ou slug…",
    "admin.translations.filter.verifs": "Filtrer par affirmation ou slug…",
    "admin.translations.action.edit": "Modifier",
    "admin.translations.action.add": "Ajouter",
    "admin.translations.desc.articles": "Cliquez sur un article pour gérer ses traductions langue par langue",
    "admin.translations.desc.verifs": "Cliquez sur une vérification pour gérer ses traductions langue par langue",
    "admin.translations.noArticle": "Aucun article",
    "admin.translations.noVerif": "Aucune vérification",
    "admin.translations.noResult": "Aucun résultat",
    "admin.translations.modal.article.ref": "Article :",
    "admin.translations.modal.verif.ref": "Vérification :",
    "admin.translations.modal.titleRequired": "Le titre est requis.",
    "admin.translations.modal.affirmationRequired": "L'affirmation est requise.",
    "admin.translations.modal.error": "Erreur.",

    // ── Détail article ────────────────────────────────────────────────────────
    "admin.detail.article.notFound": "Article introuvable",
    "admin.detail.article.notExists": "Cet article n'existe pas ou a été supprimé.",
    "admin.detail.article.back": "Retour aux articles",
    "admin.detail.article.backLink": "Retour",
    "admin.detail.article.meta.status": "Statut",
    "admin.detail.article.meta.category": "Catégorie",
    "admin.detail.article.meta.signature": "Signature",
    "admin.detail.article.meta.slug": "Slug",
    "admin.detail.article.meta.readingTime": "Temps de lecture",
    "admin.detail.article.meta.publishedAt": "Publié le",
    "admin.detail.article.meta.updatedAt": "Modifié le",
    "admin.detail.article.meta.tags": "Étiquettes",
    "admin.detail.article.meta.subtitle": "Sous-titre",
    "admin.detail.article.meta.resume": "Résumé",
    "admin.detail.article.meta.body": "Corps",
    "admin.detail.article.likes": "J'aime",
    "admin.detail.article.comments.title": "Commentaires ({count})",
    "admin.detail.article.comments.empty": "Aucun commentaire pour cet article.",
    "admin.detail.article.coverImage.loadError": "L'image a été enregistrée mais n'a pas pu être chargée depuis cette adresse (fichier privé, supprimé, ou URL invalide).",
    "admin.detail.article.noMedia": "Aucun média attaché à cet article",
    "admin.detail.article.onlyTitle": "Seul le titre a été renseigné.",
    "admin.detail.article.trad.new": "Nouvelle traduction pour cet article",

    // ── Détail vérification ───────────────────────────────────────────────────
    "admin.detail.verif.title": "Détail vérification",
    "admin.detail.verif.subtitleFmt": "Vérification · {format}",
    "admin.detail.verif.notFound": "Vérification introuvable",
    "admin.detail.verif.notExists": "Cette vérification n'existe pas ou a été supprimée.",
    "admin.detail.verif.back": "Retour aux vérifications",
    "admin.detail.verif.backLink": "Retour",
    "admin.detail.verif.meta.verdict": "Verdict",
    "admin.detail.verif.meta.format": "Format",
    "admin.detail.verif.meta.slug": "Slug",
    "admin.detail.verif.meta.date": "Date",
    "admin.detail.verif.meta.explication": "Explication courte (FR)",
    "admin.detail.verif.onlyAffirmation": "Seule l'affirmation a été renseignée.",
    "admin.detail.verif.trad.new": "Nouvelle traduction pour cette vérification",
    "admin.detail.verif.trad.edit": "Modification de la traduction existante",
    "admin.detail.verif.field.affirmation": "Affirmation",
    "admin.detail.verif.field.explication": "Explication courte",
    "admin.detail.verif.field.ceQuiCircule": "Ce qui circule",
    "admin.detail.verif.field.ceQueNousAvonsVerifie": "Ce que nous avons vérifié",
    "admin.detail.verif.field.preuves": "Preuves",
    "admin.detail.verif.field.pourquoiImportant": "Pourquoi c'est important",
    "admin.detail.verif.field.partageSecurise": "Partage sécurisé",

    // ── Pièces jointes (vérification) ─────────────────────────────────────────
    "admin.detail.verif.attachments.title": "Pièces jointes",
    "admin.detail.verif.attachments.desc": "Joindre un PDF, image, audio ou vidéo à la vérification",
    "admin.detail.verif.attachments.file": "Fichier",
    "admin.detail.verif.attachments.type.video": "Vidéo",
    "admin.detail.verif.attachments.label": "Libellé",
    "admin.detail.verif.attachments.labelPlaceholder": "Ex. Capture d’écran",
    "admin.detail.verif.attachments.add": "Ajouter la pièce jointe",
    "admin.detail.verif.attachments.viewFile": "Voir le fichier",
    "admin.detail.verif.attachments.empty": "Aucune pièce jointe pour le moment.",
    "admin.detail.verif.attachments.uploadError": "Impossible d’ajouter la pièce jointe.",

    // ── Live public (clés supplémentaires) ────────────────────────────────────
    "live.noEvents": "Aucun direct en cours.",
    "live.noUpdates": "Aucune mise à jour pour l'instant.",
    "live.closed": "Direct clôturé",
    "live.startedAt": "Démarré le {date}",
    "live.multipleEvents": "{count} directs en cours",

    // ── Admin Direct (clés supplémentaires) ───────────────────────────────────
    "admin.live.newEvent": "Nouveau direct",
    "admin.live.noEvents": "Aucun direct. Cliquez sur « Nouveau direct » pour commencer.",
    "admin.live.eventsList": "Directs en cours et passés",
    "admin.live.create": "Créer le direct",
    "admin.live.titleFr": "Titre en français *",
    "admin.live.titleEn": "Titre en anglais (optionnel)",
    "admin.live.closed": "Clôturé",
    "admin.live.closedAt": "Fermé le {date}",
    "admin.live.startedAt": "Démarré le {date}",
    "admin.live.updates": "{count} mises à jour",
    "admin.live.pinned": "Épingler",
    "admin.live.publishUpdate": "Publier",
    "admin.live.createSuccess": "Direct créé.",
    "admin.live.closeSuccess": "Direct clôturé.",
    "admin.live.updateSuccess": "Mise à jour publiée.",

    // ── Newsletter public ─────────────────────────────────────────────────────
    "newsletter.success": "Abonnement confirmé.",
    "newsletter.error": "Erreur lors de l'abonnement.",
    "newsletter.already_subscribed": "Cet email est déjà inscrit à la newsletter.",
    "newsletter.invalid_email": "Adresse email invalide.",
    "newsletter.alreadySubscribed": "Cet email est déjà abonné.",
    "newsletter.unsubscribe.success": "Désabonnement effectué.",
    "newsletter.unsubscribe": "Se désabonner",
    "newsletter.unsubscribeLink": "Se désabonner de la newsletter",
    "newsletter.backToSubscribe": "← Revenir à l'abonnement",
    "newsletter.categoriesHint": "Catégories qui vous intéressent (facultatif — sinon, tout) :",

    // ── Admin Newsletter ──────────────────────────────────────────────────────
    "admin.newsletter.title": "Newsletter",
    "admin.newsletter.subtitle": "Gestion des abonnés",
    "admin.newsletter.subscribers": "Abonnés",
    "admin.newsletter.col.email": "Email",
    "admin.newsletter.col.canal": "Canal",
    "admin.newsletter.col.langue": "Langue",
    "admin.newsletter.col.date": "Date d'inscription",
    "admin.newsletter.filter.active": "Actifs",
    "admin.newsletter.filter.inactive": "Désabonnés",
    "admin.newsletter.unsubscribe": "Désabonner",
    "admin.newsletter.empty": "Aucun abonné.",
    "admin.newsletter.total": "{count} abonnés",
  },
  en: {
    "brand.tagline": "Verified information. Protected sources.",
    "nav.home": "Home",
    "nav.live": "Live",
    "nav.factcheck": "Fact-check",
    "nav.explainers": "Media literacy",
    "nav.categories": "Categories",
    "nav.rubriques": "Sections",
    "nav.about": "About",
    "nav.editorial": "Editorial Guidelines",
    "nav.contact": "Contact",
    "nav.languages": "Languages",
    "nav.search": "Search",
    "nav.profile": "Follow",
    "nav.verify": "Verify",
    "cta.followAlerts": "Follow alerts",
    "cta.subscribe": "Subscribe",
    "verificationRequest.cta": "Request verification",
    "verificationRequest.title": "Submit content for verification",
    "verificationRequest.description": "Seen something that looks doubtful? Describe it below, with an attachment if you have one. No account needed.",
    "verificationRequest.textLabel": "What would you like to report?",
    "verificationRequest.textPlaceholder": "Describe the content, where you saw it, and why it seems doubtful…",
    "verificationRequest.attachments": "Attachment (optional)",
    "verificationRequest.addAttachment": "Add an image, audio, or video file",
    "verificationRequest.attachmentHint": "20 MB maximum per file.",
    "verificationRequest.wantsContact": "I'd like to be contacted about this report",
    "verificationRequest.name": "Name",
    "verificationRequest.email": "Email",
    "verificationRequest.phone": "Phone",
    "verificationRequest.submit": "Submit",
    "verificationRequest.textRequired": "Please describe the content to verify.",
    "verificationRequest.contactRequired": "Please provide at least one way to contact you.",
    "verificationRequest.success": "Your report has been sent, thank you.",
    "verificationRequest.submitError": "Failed to send, please try again.",
    "verificationRequest.uploadError": "Failed to upload the file.",
    "cta.share": "Share",
    "cta.readMore": "Read more",
    "cta.listen": "Listen",
    "cta.followLive": "Follow live",
    "alert.banner": "Verified alert",
    "section.latest": "Latest news",
    "section.factcheck": "Verification & fact-checking",
    "section.live": "Live updates",
    "section.explainers": "Explainers",
    "section.in60": "In 60 seconds",
    "section.in60.desc": "Audio and video bulletins presented by our digital presenters.",
    "section.follow": "Receive alerts",
    "section.follow.desc": "Newsletter, WhatsApp and Telegram. Verified information, no spam.",
    "section.related": "Related reading",

    // ── Comments ────────────────────────────────────────────────────────────
    "comments.title": "Comments",
    "comments.likes": "Likes",
    "comments.empty": "No comments yet.",
    "comments.form.name": "Your name",
    "comments.form.placeholder": "Your comment…",
    "comments.form.submit": "Post comment",
    "comments.form.required": "Name and comment are required.",
    "comments.form.error": "Unable to send the comment.",
    "comments.form.sent": "Comment posted, thank you!",
    "label.byDesk": "Ramba News Desk",
    "label.partnerDesk": "Partner Desk",
    "label.readingTime": "min read",
    "label.published": "Published",
    "label.updated": "Updated",
    "label.tags": "Tags",
    "label.languages": "Available in",
    "label.aiPresenter": "AI digital presenter",
    "label.audioOnly": "Audio only",
    "label.lowData": "Low-data mode",
    "label.sourceProtected": "Sources are protected.",
    "label.correction": "Correction",
    "verdict.false": "False",
    "verdict.misleading": "Misleading",
    "verdict.unproven": "Unproven",
    "verdict.true": "True",
    "verdict.context": "Needs context",
    "priority.confirmed": "Confirmed",
    "priority.developing": "Developing",
    "priority.correction": "Correction",
    "priority.alert": "Alert",
    "footer.about": "About",
    "footer.editorial": "Editorial Guidelines",
    "footer.contact": "Contact",
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.languages": "Languages",
    "cookies.title": "Cookie settings",
    "cookies.description": "We use cookies to measure audience and improve your experience on Ramba News.",
    "cookies.learnMore": "Learn more",
    "cookies.accept": "Accept",
    "cookies.reject": "Reject",
    "footer.copy": "© {year} Ramba News — Secure digital media platform.",
    "search.placeholder": "Search articles, fact-checks, live updates…",
    "countryBar.all": "All countries",
    "filter.all": "All",
    "filter.category": "Category",
    "filter.date": "Date",
    "filter.type": "Type",
    "filter.contentType": "Content type",
    "filter.verdict": "Verdict",
    "filter.language": "Language",
    "form.email": "Email address",
    "form.password": "Password",
    "form.submit": "Submit",
    "form.message": "Message",
    "form.name": "Name (optional)",
    "form.confidentialTip": "Confidential tip",
    "admin.title": "Editorial administration",
    "admin.signin": "Secure sign-in",
    "admin.noPublic": "Access is restricted to authorized members. No public registration.",
    "admin.accessCode": "Access code",
    "admin.connecting": "Connecting…",
    "admin.sessionSecurity": "Encrypted session. All attempts are logged.",
    "admin.authError": "Incorrect email or password.",
    "brand.newsroom": "Secure digital newsroom",
    "footer.description": "Ramba News is a secure digital media platform dedicated to verified information, responsible journalism and public resilience against disinformation in West Africa.",
    "footer.legal": "Legal",
    "footer.admin": "Editorial space",
    "form.email.placeholder": "Enter your email address",
    "contact.heading": "Contact us",
    "contact.description": "For any editorial question or confidential tip, use the secure form below.",
    "contact.security": "Your information is treated confidentially. No personal contact details of our team are publicly exposed for security reasons.",
    "contact.general": "General contact",
    "contact.tips": "Tips",
    "about.heading": "A secure digital media platform",
    "about.intro": "Ramba News is a secure, online and distributed digital media platform dedicated to verified information, responsible journalism and public resilience against disinformation. Its editorial model is designed to protect sources, contributors and partners, while providing the public with reliable and accessible information.",
    "about.card.verified.title": "Verified information",
    "about.card.verified.body": "Systematic cross-referencing of multiple independent sources before publication.",
    "about.card.sources.title": "Protected sources",
    "about.card.sources.body": "Secure editorial workflows and rigorous protection of contributors.",
    "about.card.multilingual.title": "Multilingual",
    "about.card.multilingual.body": "French, English, and local language versions where they broaden access.",
    "about.approach.title": "Our approach",
    "about.approach.body": "We operate as a secure and distributed digital newsroom. Our workflows protect contributors and partners, and our digital presenters (AI avatars) are transparently identified as such — never as human journalists.",
    "editorial.heading": "Our principles",
    "editorial.description": "These principles guide our daily work and structure our editorial process.",
    "editorial.accuracy.title": "Accuracy",
    "editorial.accuracy.body": "We only publish what we have verified. When uncertain, we say so.",
    "editorial.verification.title": "Verification",
    "editorial.verification.body": "Systematic cross-referencing of multiple independent sources before publication.",
    "editorial.independence.title": "Independence",
    "editorial.independence.body": "Our editorial decisions are made without external pressure.",
    "editorial.neutrality.title": "Neutrality",
    "editorial.neutrality.body": "We present facts with rigor, without advocacy.",
    "editorial.sources.title": "Source protection",
    "editorial.sources.body": "Contributors and sensitive sources are protected under secure protocols.",
    "editorial.nohate.title": "No hate speech",
    "editorial.nohate.body": "We refuse any publication inciting hatred, discrimination or violence.",
    "editorial.nopropaganda.title": "No propaganda",
    "editorial.nopropaganda.body": "Our role is to inform, not to relay biased narratives.",
    "editorial.ai.title": "Responsible AI use",
    "editorial.ai.body": "Our digital presenters are labeled as such and subject to human validation before publication.",
    "editorial.correction.title": "Correction policy",
    "editorial.correction.body": "Any identified error is corrected publicly with a dated note.",
    "languages.heading": "Accessible information",
    "languages.description": "Ramba News publishes in French and English, with versions available in Bambara, Hausa and Mooré to broaden access to verified and understandable information.",
    "languages.content": "Articles, audio and video",
    "languages.note": "The main site interface is available in French and English. Content may include additional translated versions depending on topics and audience needs.",
    "error.404.title": "Page not found",
    "error.404.desc": "The requested page does not exist or has been moved.",
    "error.404.cta": "Back to home",
    "error.load": "Unable to load content.",
    "cta.retry": "Retry",
    "home.empty.articles": "No articles published yet.",
    "home.empty.verifications": "No verifications published yet.",
    "home.empty.live": "No live updates.",
    "common.loading": "Loading…",
    "cta.copy": "Copy",

    // ── Live public ───────────────────────────────────────────────────────────
    "live.badge": "Live",
    "live.updated": "Feed updated with each verification",
    "live.intro": "Chronological updates. No unverified information. Corrections are explicitly flagged.",
    "live.updatesCount": "{count} updates",
    "live.pinned": "Pinned",

    // ── Article public ────────────────────────────────────────────────────────
    "article.backHome": "← Back to home",
    "article.verified": "Verified",
    "article.aiAssisted.badge": "AI-assisted content",
    "article.aiAssisted.dialogTitle": "Use of AI in this article",
    "article.aiAssisted.charte": "This article was written with the assistance of an artificial intelligence tool.\n\nAt Ramba News, AI may help with research, formatting, or translation, but it never replaces human verification: every fact, quote, and source is checked by our newsroom before publication.\n\nEditorial responsibility for this article remains entirely human.",
    "article.listen": "Listen to article",
    "article.listening": "Playing…",
    "article.copied": "Copied!",
    "article.copyLink": "Copy link",
    "article.correctionNote": "A data point was adjusted after re-verification.",
    "article.fallback.p1": "Our editorial team systematically applies a verification protocol: cross-referencing multiple independent sources, analysing available public documents, and editorial review before publication.",
    "article.fallback.p2": "The elements presented in this article were verified at the time of publication. Any significant development will be flagged with a dated update.",

    // ── Category public ───────────────────────────────────────────────────────
    "category.eyebrow": "Category",
    "category.empty": "No content published for this category yet.",
    "rubrique.eyebrow": "Section",
    "rubrique.sidebar.all": "All categories",

    // ── Fact-check public ─────────────────────────────────────────────────────
    "factcheck.intro": "Each fact-check follows a public methodology: sources, method, evidence and secure share version.",
    "factcheck.loadError": "Unable to load fact-checks.",
    "factcheck.emptyFilter": "No fact-checks for this filter.",
    "factcheck.notFound": "Fact-check not found or loading error.",
    "factcheck.backAll": "← All fact-checks",
    "factcheck.section.circulating": "What's circulating",
    "factcheck.section.verified": "What we verified",
    "factcheck.section.evidence": "Evidence & method",
    "factcheck.section.why": "Why it matters",
    "factcheck.attachments.title": "Attachments",
    "factcheck.attachments.document": "Open document",
    "factcheck.safeShareTitle": "Secure share version",
    "factcheck.filter.context": "Context",

    // ── Search public ─────────────────────────────────────────────────────────
    "search.filter.24h": "24h",
    "search.filter.7days": "7 days",
    "search.filter.30days": "30 days",
    "search.articles": "Articles",
    "search.verifications": "Verifications",

    // ── Privacy ───────────────────────────────────────────────────────────────
    "privacy.heading": "Privacy",
    "privacy.body": "Ramba News applies a strict data protection policy. We only collect information strictly necessary for the service and do not share any identifying data with third parties for commercial purposes.",

    // ── Terms ─────────────────────────────────────────────────────────────────
    "terms.heading": "Terms of use",
    "terms.body": "Use of this platform implies acceptance of these terms. Published content is protected by applicable intellectual property laws. Any reproduction must cite Ramba News as the source.",

    // ── Weekly recap ──────────────────────────────────────────────────────────
    "recap.heading": "This week's recap",
    "recap.subtitle": "Every article published in the last 7 days, at a glance.",
    "recap.empty": "No article published this week yet.",

    // ── Newsletter unsubscribe ────────────────────────────────────────────────
    "unsubscribe.heading": "Unsubscribe from the newsletter",
    "unsubscribe.intro": "You will stop receiving Ramba News newsletter emails at the following address:",
    "unsubscribe.emailLabel": "Email address",
    "unsubscribe.emailPlaceholder": "you@example.com",
    "unsubscribe.confirm": "Unsubscribe",
    "unsubscribe.success": "You've been unsubscribed. You will no longer receive our emails.",
    "unsubscribe.error": "Could not unsubscribe you right now. Please try again later.",
    "unsubscribe.notFound": "This email address is not subscribed to the newsletter.",

    // ── Hero ──────────────────────────────────────────────────────────────────
    "hero.noArticle": "No articles available",
    "hero.firstArticleHint": "The first published article will appear here.",
    "hero.verified": "Verified",
    "hero.noTitle": "Untitled",

    // ── Editorial statuses ────────────────────────────────────────────────────
    "status.published": "Published",
    "status.draft": "Draft",
    "status.inReview": "In review",
    "status.pending": "Pending",
    "status.scheduled": "Scheduled",
    "status.archived": "Archived",
    "status.rejected": "Rejected",
    "status.rendered": "Rendered",
    "status.ready": "Ready",

    // ── Admin Analytics ───────────────────────────────────────────────────────
    "admin.analytics.title": "Analytics",
    "admin.analytics.subtitle": "Audience and engagement ",
    "admin.analytics.stat.views24h": "Views 24h",
    "admin.analytics.stat.shares": "Shares",
    "admin.analytics.stat.activeLangs": "Active languages",
    "admin.analytics.stat.lowData": "Low-data mode",
    "admin.analytics.chart.title": "Trend (14 days)",
    "admin.analytics.chart.desc": "Chart placeholder",
    "admin.analytics.topContent": "Top content",
    "admin.analytics.col.views": "Views",

    // ── Admin Live ────────────────────────────────────────────────────────────
    "admin.live.title": "Live",
    "admin.live.subtitle": "Create and manage live feeds",
    "admin.live.openFeed": "Open feed",
    "admin.live.close": "Close",
    "admin.live.newUpdate": "New verified update…",

    // ── Admin Security ────────────────────────────────────────────────────────
    "admin.security.title": "Security review",
    "admin.security.subtitle": "Mandatory validation before sensitive publication",
    "admin.security.checklist": "Editorial security checklist",
    "admin.security.checklistDesc": "Each item must be reviewed by a security reviewer",
    "admin.security.check.1": "Does this content expose a source?",
    "admin.security.check.2": "Does this content reveal a location?",
    "admin.security.check.3": "Does this content contain metadata risks?",
    "admin.security.check.4": "Does this content unnecessarily name individuals?",
    "admin.security.check.5": "Does this content create a physical, digital or legal risk?",
    "admin.security.reviewerNote": "Reviewer note: —",
    "admin.security.approve": "Approve publication",
    "admin.security.block": "Block",

    // ── Admin Sources ─────────────────────────────────────────────────────────
    "admin.sources.title": "Sources & partners",
    "admin.sources.subtitle": "Confidential zone — restricted access",
    "admin.sources.alertTitle": "Strictly confidential data",
    "admin.sources.alertBody": "No information from this section should ever appear on the public site or be shared outside the secure workflow.",
    "admin.sources.network": "Protected network",
    "admin.sources.col.code": "Code",
    "admin.sources.col.trust": "Trust level",
    "admin.sources.col.channel": "Channel",
    "admin.sources.col.risk": "Risk",
    "admin.sources.col.history": "History",

    // ── Admin Avatars ─────────────────────────────────────────────────────────
    "admin.avatars.title": "Avatar production",
    "admin.avatars.subtitle": "AI digital presenters — human validation required",
    "admin.avatars.queue": "Production queue",
    "admin.avatars.queueDesc": "Human validation required before publication",
    "admin.avatars.col.content": "Content",
    "admin.avatars.col.language": "Language",
    "admin.avatars.col.voice": "Voice",
    "admin.avatars.col.audioOnly": "Audio only",
    "admin.avatars.yes": "Yes",
    "admin.avatars.no": "No",

    // ── Admin back to site ────────────────────────────────────────────────────
    "admin.backToSite": "← Public site",

    // ── Admin navigation ──────────────────────────────────────────────────────
    "admin.nav.open": "Open menu",
    "admin.nav.dashboard": "Dashboard",
    "admin.nav.workflow": "Editorial workflow",
    "admin.nav.content": "Content",
    "admin.nav.articles": "Articles",
    "admin.article.viewAll": "View all articles",
    "admin.nav.factchecks": "Fact-checks",
    "admin.nav.analytics": "Analytics",
    "admin.nav.translations": "Translations",
    "admin.nav.roles": "Users & roles",
    "admin.nav.disinformation": "Disinformation",
    "admin.nav.settings": "Settings",
    "admin.logout": "Sign out",
    "admin.secureSession": "Secure session",

    // ── Admin common ──────────────────────────────────────────────────────────
    "admin.common.cancel": "Cancel",
    "admin.common.save": "Save",
    "admin.common.saved": "Saved",
    "admin.common.approve": "Approve",
    "admin.common.reject": "Reject",
    "admin.common.delete": "Delete",
    "admin.common.edit": "Edit",
    "admin.common.add": "Add",
    "admin.common.next": "Next",
    "admin.common.back": "Back",
    "admin.common.skip": "Skip",
    "admin.common.new": "New",
    "admin.common.search": "Search…",
    "admin.common.loading": "Loading…",
    "admin.common.publish": "Publish",
    "admin.common.page": "Page {page} / {total}",
    "admin.common.noResult": "No results",
    "admin.common.translated": "Translated",
    "admin.common.missing": "Missing",
    "admin.common.metadata": "Metadata",
    "admin.common.translations": "Translations",
    "admin.common.multilingual": "Multilingual completion",
    "admin.common.col.title": "Title",
    "admin.common.col.type": "Type",
    "admin.common.col.status": "Status",
    "admin.common.col.updated": "Updated",
    "admin.common.col.date": "Date",
    "admin.common.col.actions": "Actions",
    "admin.common.col.languages": "Languages",
    "admin.common.col.verdict": "Verdict",
    "admin.common.col.format": "Format",
    "admin.common.col.claim": "Claim",
    "admin.common.langues": "{count} / {total} languages translated — {pct}% complete",
    "admin.common.upload": "Uploading…",
    "admin.common.sendToS3": "Sending to S3…",
    "admin.common.saveError": "Error during save.",
    "admin.common.deleteError": "Error during deletion.",
    "admin.common.uploadError": "Upload error.",

    "admin.users.title": "Users & roles",
    "admin.users.subtitle": "Invite users and manage their access rights",
    "admin.users.invite.title": "Invite a user",
    "admin.users.invite.emailPlaceholder": "first.last@example.com",
    "admin.users.invite.submit": "Invite",
    "admin.users.role.admin": "Admin",
    "admin.users.role.redacteur": "Writer",
    "admin.users.role.partenaire": "Partner",
    "admin.users.col.email": "Email",
    "admin.users.col.role": "Role",
    "admin.users.col.expires": "Expires on",
    "admin.users.col.nom": "Name",
    "admin.users.col.roles": "Roles",
    "admin.users.col.code": "Code",
    "admin.users.invitations.title": "Invitations ({count})",
    "admin.users.invitations.empty": "No invitations sent",
    "admin.users.invitations.status.pending": "Pending",
    "admin.users.invitations.status.accepted": "Accepted",
    "admin.users.invitations.status.expired": "Expired",
    "admin.users.invitations.status.cancelled": "Cancelled",
    "admin.users.invitations.cancel": "Cancel",
    "admin.users.list.title": "Users ({count})",
    "admin.users.status.blocked": "Blocked",
    "admin.users.status.active": "Active",
    "admin.users.status.attempts": "{count} attempts",
    "admin.users.lock.label": "Locked {minutes} min",
    "admin.users.lock.tooltip": "Temporary anti-bruteforce lock (independent from the status above): login still fails even though the account is active.",
    "admin.users.lock.action": "Lift lock",
    "admin.users.action.block": "Block",
    "admin.users.action.unblock": "Unblock",
    "admin.users.deleteConfirm.title": "Delete this user?",
    "admin.users.deleteConfirm.description": "“{email}” will be permanently deleted.",
    "admin.users.unblockDialog.title": "Unblock {email}",
    "admin.users.unblockDialog.description": "This message will be emailed to the user to let them know their account was unblocked.",
    "admin.users.unblockDialog.placeholder": "E.g.: Your account has been unblocked, please try logging in again…",
    "admin.users.unblockDialog.submit": "Unblock and send",
    "admin.users.toast.actionError": "Action failed",
    "admin.users.toast.blocked": "{email} blocked",
    "admin.users.toast.lockLifted": "Temporary lock lifted for {email}",
    "admin.users.toast.unblocked": "{email} unblocked, an email was sent",
    "admin.users.toast.invitationCancelled": "Invitation to {email} cancelled",
    "admin.users.toast.invitationCancelError": "Could not cancel this invitation",
    "admin.users.toast.deleted": "{email} deleted",
    "admin.users.toast.deleteError": "Could not delete this user",
    "admin.users.toast.invitationSent": "Invitation sent to {email}",
    "admin.users.toast.inviteError": "Could not send the invitation",

    // ── Dashboard ─────────────────────────────────────────────────────────────
    "admin.dashboard.subtitle": "Overview of the secure newsroom",
    "admin.dashboard.publishedToday": "Published today",
    "admin.dashboard.publishedToday.hint": "+2 vs yesterday",
    "admin.dashboard.pendingReview": "Pending review",
    "admin.dashboard.pendingReview.hint": "3 under review",
    "admin.dashboard.verif": "Verifications in progress",
    "admin.dashboard.verifiedAlerts": "Verified alerts",
    "admin.dashboard.recentActivity": "Recent editorial activity",
    "admin.dashboard.recentActivity.desc": "Latest content in the workflow",
    "admin.dashboard.security": "Security",
    "admin.dashboard.security.desc": "No active incident",
    "admin.dashboard.security.nominal": "All nominal",
    "admin.dashboard.security.audit": "Last audit: 2 hours ago",
    "admin.dashboard.live": "Live",
    "admin.dashboard.live.desc": "Monitoring in progress",
    "admin.dashboard.live.open": "1 live open",
    "admin.dashboard.live.updates": "5 verified updates",
    "admin.dashboard.audience": "Audience",
    "admin.dashboard.audience.desc": "All-time",
    "admin.dashboard.audience.views": "Views",
    "admin.dashboard.audience.reads": "Reads",
    "admin.dashboard.audience.shares": "Shares",
    "admin.dashboard.audience.visitors": "Unique visitors",
    "admin.dashboard.pendingVerif": "Pending verifications",
    "admin.dashboard.col.proposedVerdict": "Proposed verdict",
    "admin.dashboard.col.validatedBy": "Validated by",
    "admin.dashboard.noTranslation": "No translation",

    // ── Workflow ──────────────────────────────────────────────────────────────
    "admin.workflow.subtitle": "Validation and security pipeline",
    "admin.workflow.steps": "Workflow steps",
    "admin.workflow.pipeline": "Content in the pipeline",
    "admin.workflow.col.step": "Step",
    "admin.workflow.col.assigned": "Assigned to",
    "admin.workflow.state.draft": "Draft",
    "admin.workflow.state.submitted": "Submitted",
    "admin.workflow.state.verifying": "Under verification",
    "admin.workflow.state.security": "Security review",
    "admin.workflow.state.translation": "Ready for translation",
    "admin.workflow.state.avatar": "Ready for avatar",
    "admin.workflow.state.scheduled": "Scheduled",
    "admin.workflow.state.published": "Published",
    "admin.workflow.state.corrected": "Corrected",
    "admin.workflow.state.archived": "Archived",
    "admin.workflow.team.editorial": "Editorial",
    "admin.workflow.team.verif": "Verif.",
    "admin.workflow.team.security": "Security",
    "admin.workflow.team.translation": "Trans.",

    // ── Roles & permissions ───────────────────────────────────────────────────
    "admin.roles.subtitle": "Access matrix",
    "admin.roles.matrix": "Permissions matrix",
    "admin.roles.col.role": "Role",
    "admin.roles.superAdmin": "Super Admin",
    "admin.roles.editor": "Editor-in-Chief",
    "admin.roles.writer": "Writer",
    "admin.roles.verifier": "Verifier",
    "admin.roles.translator": "Translator",
    "admin.roles.avatarProd": "Avatar producer",
    "admin.roles.securityReviewer": "Security reviewer",
    "admin.roles.analyst": "Analyst",
    "admin.roles.reader": "Reader",
    "admin.roles.perm.read": "Read",
    "admin.roles.perm.edit": "Edit",
    "admin.roles.perm.publish": "Publish",
    "admin.roles.perm.delete": "Delete",
    "admin.roles.perm.security": "Security",
    "admin.roles.perm.admin": "Admin",

    // ── Disinformation ────────────────────────────────────────────────────────
    "admin.disinfo.title": "Disinformation monitoring",
    "admin.disinfo.subtitle": "Viral narratives and risk level",
    "admin.disinfo.tracked": "Tracked narratives",
    "admin.disinfo.response": "Response",

    // ── Settings ──────────────────────────────────────────────────────────────
    "admin.settings.subtitle": "Platform configuration",
    "admin.settings.configurable": "Configurable section. Connect backend to activate.",
    "admin.settings.identity.title": "Site identity",
    "admin.settings.identity.desc": "Name, description, logo, favicon",
    "admin.settings.languages.title": "Languages",
    "admin.settings.languages.desc": "FR, EN, BM, HA, MOS — configurable public visibility",
    "admin.settings.categories.title": "Categories",
    "admin.settings.categories.desc": "Editorial category management",

    // ── Categories page (admin) ───────────────────────────────────────────────
    "admin.nav.categories": "Categories",
    "admin.categories.subtitle": "Create, edit or delete editorial categories",
    "admin.categories.form.title": "Add / edit a category",
    "admin.categories.form.slugPlaceholder": "e.g. governance",
    "admin.categories.form.labelFr": "Label FR",
    "admin.categories.form.labelEn": "Label EN",
    "admin.categories.form.rubrique": "Section",
    "admin.categories.form.rubriqueNone": "None",
    "admin.categories.list.title": "Available categories ({count})",
    "admin.categories.list.empty": "No categories yet.",
    "admin.categories.deleteConfirm": "Delete this category?",

    // ── Tags page (admin) ────────────────────────────────────────────────────
    "admin.nav.etiquettes": "Tags",
    "admin.etiquettes.subtitle": "Create, edit or delete tags",
    "admin.etiquettes.form.title": "Add / edit a tag",
    "admin.etiquettes.form.slugPlaceholder": "e.g. elections",
    "admin.etiquettes.list.title": "Available tags ({count})",
    "admin.etiquettes.list.empty": "No tags yet.",
    "admin.etiquettes.deleteConfirm.title": "Delete this tag?",
    "admin.etiquettes.deleteConfirm.desc": "This action is irreversible. It will be removed from any article using it.",

    // ── Rubriques page (admin) ────────────────────────────────────────────────
    "admin.nav.rubriques": "Sections",
    "admin.rubriques.subtitle": "Create, edit or delete sections (groupings of categories)",
    "admin.rubriques.form.title": "Add / edit a section",
    "admin.rubriques.form.slugPlaceholder": "e.g. politics",
    "admin.rubriques.form.nom": "Name",
    "admin.rubriques.form.nomEn": "Name (EN)",
    "admin.rubriques.form.ordre": "Order",
    "admin.rubriques.form.actif": "Active",
    "admin.rubriques.list.title": "Available sections ({count})",
    "admin.rubriques.list.empty": "No sections yet.",
    "admin.rubriques.inactiveBadge": "inactive",
    "admin.rubriques.deleteConfirm.title": "Delete this section?",
    "admin.rubriques.deleteConfirm.desc": "This action is irreversible. Attached categories will not be deleted, but will lose their link to this section.",
    "admin.translations.deleteConfirm.title": "Delete this translation?",
    "admin.translations.deleteConfirm.desc": "This action is irreversible.",
    "admin.translations.split.hint": "French version fixed on the left, for reference while translating",
    "admin.detail.article.media.deleteConfirm.title": "Delete this media?",
    "admin.detail.article.media.deleteConfirm.desc": "This action is irreversible.",
    "admin.detail.verif.attachments.deleteConfirm.title": "Delete this attachment?",
    "admin.detail.verif.attachments.deleteConfirm.desc": "This action is irreversible.",

    // ── Admin session ─────────────────────────────────────────────────────────
    "admin.session.expiringSoon.title": "Your session is about to expire",
    "admin.session.expiringSoon.desc": "You will be automatically logged out in",
    "admin.session.logoutNow": "Log out",
    "admin.session.extend": "Extend session",
    "admin.settings.workflow.title": "Workflow",
    "admin.settings.workflow.desc": "Validation steps and approval rules",
    "admin.settings.notifications.title": "Notifications",
    "admin.settings.notifications.desc": "Email, WhatsApp, Telegram alerts",
    "admin.settings.security.title": "Security",
    "admin.settings.security.desc": "2FA, sessions, audit log, IP allowlist",
    "admin.settings.api.title": "API",
    "admin.settings.api.desc": "Keys and webhooks ",
    "admin.settings.cms.title": "CMS",
    "admin.settings.cms.desc": "CMS integrations ",

    // ── Articles ──────────────────────────────────────────────────────────────
    "admin.content.subtitle": "Create, edit and publish articles",
    "admin.content.noArticle": "No articles found",
    "admin.article.new": "New article",
    "admin.article.step.meta": "Metadata",
    "admin.article.step.translation": "French translation",
    "admin.article.step.media": "Media",
    "admin.article.rubrique": "Section",
    "admin.article.selectRubrique": "Select a section.",
    "admin.article.categories": "Categories",
    "admin.article.select": "Select…",
    "admin.article.signature": "Signature",
    "admin.article.tags": "Tags",
    "admin.article.noTags": "No tags available",
    "admin.article.titre": "Title",
    "admin.article.sousTitre": "Subtitle",
    "admin.article.corps": "Article body",
    "admin.article.commentsAllowed": "Allow comments",
    "admin.article.aiAssisted": "Written with AI assistance",
    "admin.article.sponsored": "Sponsored article",
    "admin.article.new.title": "Add an article",
    "admin.article.new.subtitle": "Create a new article — media and additional languages are managed after creation",
    "admin.article.new.meta": "General information",
    "admin.article.new.publication": "Publication",
    "admin.article.schedule": "Schedule for",
    "admin.article.dateCreation": "Creation date",
    "admin.article.dateModification": "Last modified date",
    "admin.article.datePublie": "Publication date",
    "admin.article.publish": "Publish",
    "admin.article.schedulePublish": "Schedule",
    "admin.article.saveDraft": "Save as draft",
    "admin.article.auteur": "Author",
    "admin.article.auteurNone": "— Choose —",
    "admin.article.coverImage": "Cover image",
    "admin.article.chooseCover": "Choose from media library…",
    "admin.article.metaTitre": "Meta title",
    "admin.article.metaDescription": "Meta description",
    "admin.article.new.content": "Content (French)",
    "admin.editor.saving": "Saving…",
    "admin.editor.saved": "Saved",
    "admin.article.readingTime": "Reading time (in minutes)",
    "admin.article.addMedia": "Add media",
    "admin.article.noMedia": "No media added",
    "admin.article.mediaOptional": "This step is optional. You can add media now or skip this step.",
    "admin.article.inProgress": "• In progress…",
    "admin.article.filesCount": "Files ({count})",
    "admin.article.rubrique.label": "Category:",
    "admin.article.type.label": "Type:",
    "admin.article.waitUpload": "Wait for uploads to complete before creating.",
    "admin.article.selectCategory": "Select a category.",
    "admin.article.titleRequired": "French title is required.",
    "admin.article.uploadError": "Upload error",
    "admin.article.creationError": "Error during creation.",
    "admin.article.delete.title": "Delete this article?",
    "admin.article.delete.desc": "will be permanently deleted, along with all its translations.",
    "admin.article.trad.edit": "Editing existing translation",
    "admin.article.trad.new": "New translation for this article",
    "admin.article.trad.titleRequired": "Title is required.",

    // ── Fact-checks ───────────────────────────────────────────────────────────
    "admin.factchecks.subtitle": "Fact-check editor and library",
    "admin.factchecks.new": "New",
    "admin.factchecks.all": "All",
    "admin.factchecks.empty": "No fact-checks found",
    "admin.factcheck.new": "New fact-check",
    "admin.factcheck.step.meta": "Metadata",
    "admin.factcheck.step.translation": "French translations",
    "admin.factcheck.verdict": "Verdict *",
    "admin.factcheck.format": "Format",
    "admin.factcheck.rubrique": "Category",
    "admin.factcheck.none": "None",
    "admin.factcheck.affirmationRequired": "French claim is required.",
    "admin.factcheck.creationError": "Error during creation.",
    "admin.factcheck.verdict.label": "Verdict:",
    "admin.factcheck.format.label": "Format:",
    "admin.factcheck.rubrique.label": "Category:",
    "admin.factcheck.field.affirmation": "Claim *",
    "admin.factcheck.field.explication": "Short explanation",
    "admin.factcheck.field.ceQuiCircule": "What's circulating",
    "admin.factcheck.field.ceQueNousAvonsVerifie": "What we verified",
    "admin.factcheck.field.preuves": "Evidence",
    "admin.factcheck.field.pourquoiImportant": "Why it matters",
    "admin.factcheck.field.partageSecurise": "Secure share version",
    "admin.factcheck.field.partageSecurise2": "Secure share",
    "admin.factcheck.delete.title": "Delete this fact-check?",
    "admin.factcheck.delete.desc": "will be permanently deleted.",

    // ── Translations page ─────────────────────────────────────────────────────
    "admin.translations.subtitle": "Track and manage multilingual versions — articles and fact-checks",
    "admin.translations.filter.articles": "Filter by title or slug…",
    "admin.translations.filter.verifs": "Filter by claim or slug…",
    "admin.translations.action.edit": "Edit",
    "admin.translations.action.add": "Add",
    "admin.translations.desc.articles": "Click an article to manage translations by language",
    "admin.translations.desc.verifs": "Click a fact-check to manage translations by language",
    "admin.translations.noArticle": "No articles",
    "admin.translations.noVerif": "No fact-checks",
    "admin.translations.noResult": "No results",
    "admin.translations.modal.article.ref": "Article:",
    "admin.translations.modal.verif.ref": "Fact-check:",
    "admin.translations.modal.titleRequired": "Title is required.",
    "admin.translations.modal.affirmationRequired": "Claim is required.",
    "admin.translations.modal.error": "Error.",

    // ── Article detail ────────────────────────────────────────────────────────
    "admin.detail.article.notFound": "Article not found",
    "admin.detail.article.notExists": "This article doesn't exist or was deleted.",
    "admin.detail.article.back": "Back to articles",
    "admin.detail.article.backLink": "Back",
    "admin.detail.article.meta.status": "Status",
    "admin.detail.article.meta.category": "Category",
    "admin.detail.article.meta.signature": "Signature",
    "admin.detail.article.meta.slug": "Slug",
    "admin.detail.article.meta.readingTime": "Reading time",
    "admin.detail.article.meta.publishedAt": "Published on",
    "admin.detail.article.meta.updatedAt": "Updated on",
    "admin.detail.article.meta.tags": "Tags",
    "admin.detail.article.meta.subtitle": "Subtitle",
    "admin.detail.article.meta.resume": "Summary",
    "admin.detail.article.meta.body": "Body",
    "admin.detail.article.likes": "Likes",
    "admin.detail.article.comments.title": "Comments ({count})",
    "admin.detail.article.comments.empty": "No comments on this article yet.",
    "admin.detail.article.coverImage.loadError": "The image was saved but couldn't be loaded from this address (private file, deleted, or invalid URL).",
    "admin.detail.article.noMedia": "No media attached to this article",
    "admin.detail.article.onlyTitle": "Only the title was provided.",
    "admin.detail.article.trad.new": "New translation for this article",

    // ── Fact-check detail ─────────────────────────────────────────────────────
    "admin.detail.verif.title": "Fact-check detail",
    "admin.detail.verif.subtitleFmt": "Fact-check · {format}",
    "admin.detail.verif.notFound": "Fact-check not found",
    "admin.detail.verif.notExists": "This fact-check doesn't exist or was deleted.",
    "admin.detail.verif.back": "Back to fact-checks",
    "admin.detail.verif.backLink": "Back",
    "admin.detail.verif.meta.verdict": "Verdict",
    "admin.detail.verif.meta.format": "Format",
    "admin.detail.verif.meta.slug": "Slug",
    "admin.detail.verif.meta.date": "Date",
    "admin.detail.verif.meta.explication": "Short explanation (FR)",
    "admin.detail.verif.onlyAffirmation": "Only the claim was provided.",
    "admin.detail.verif.trad.new": "New translation for this fact-check",
    "admin.detail.verif.trad.edit": "Editing existing translation",
    "admin.detail.verif.field.affirmation": "Claim",
    "admin.detail.verif.field.explication": "Short explanation",
    "admin.detail.verif.field.ceQuiCircule": "What's circulating",
    "admin.detail.verif.field.ceQueNousAvonsVerifie": "What we verified",
    "admin.detail.verif.field.preuves": "Evidence",
    "admin.detail.verif.field.pourquoiImportant": "Why it matters",
    "admin.detail.verif.field.partageSecurise": "Secure share",

    // ── Attachments (fact-check) ───────────────────────────────────────────────
    "admin.detail.verif.attachments.title": "Attachments",
    "admin.detail.verif.attachments.desc": "Attach a PDF, image, audio or video to the fact-check",
    "admin.detail.verif.attachments.file": "File",
    "admin.detail.verif.attachments.type.video": "Video",
    "admin.detail.verif.attachments.label": "Label",
    "admin.detail.verif.attachments.labelPlaceholder": "E.g. Screenshot",
    "admin.detail.verif.attachments.add": "Add attachment",
    "admin.detail.verif.attachments.viewFile": "View file",
    "admin.detail.verif.attachments.empty": "No attachments yet.",
    "admin.detail.verif.attachments.uploadError": "Unable to add the attachment.",

    // ── Live public (additional keys) ─────────────────────────────────────────
    "live.noEvents": "No live feed in progress.",
    "live.noUpdates": "No updates yet.",
    "live.closed": "Live feed closed",
    "live.startedAt": "Started on {date}",
    "live.multipleEvents": "{count} live feeds in progress",

    // ── Admin Live (additional keys) ──────────────────────────────────────────
    "admin.live.newEvent": "New live feed",
    "admin.live.noEvents": "No live feed. Click 'New live feed' to start.",
    "admin.live.eventsList": "Live and past feeds",
    "admin.live.create": "Create live feed",
    "admin.live.titleFr": "Title in French *",
    "admin.live.titleEn": "Title in English (optional)",
    "admin.live.closed": "Closed",
    "admin.live.closedAt": "Closed on {date}",
    "admin.live.startedAt": "Started on {date}",
    "admin.live.updates": "{count} updates",
    "admin.live.pinned": "Pin",
    "admin.live.publishUpdate": "Publish",
    "admin.live.createSuccess": "Live feed created.",
    "admin.live.closeSuccess": "Live feed closed.",
    "admin.live.updateSuccess": "Update published.",

    // ── Newsletter public ─────────────────────────────────────────────────────
    "newsletter.success": "Subscription confirmed.",
    "newsletter.error": "Error during subscription.",
    "newsletter.already_subscribed": "This email is already subscribed to the newsletter.",
    "newsletter.invalid_email": "Invalid email address.",
    "newsletter.alreadySubscribed": "This email is already subscribed.",
    "newsletter.unsubscribe.success": "Unsubscribed successfully.",
    "newsletter.unsubscribe": "Unsubscribe",
    "newsletter.unsubscribeLink": "Unsubscribe from the newsletter",
    "newsletter.backToSubscribe": "← Back to subscribing",
    "newsletter.categoriesHint": "Topics you're interested in (optional — otherwise, everything):",

    // ── Admin Newsletter ──────────────────────────────────────────────────────
    "admin.newsletter.title": "Newsletter",
    "admin.newsletter.subtitle": "Subscriber management",
    "admin.newsletter.subscribers": "Subscribers",
    "admin.newsletter.col.email": "Email",
    "admin.newsletter.col.canal": "Channel",
    "admin.newsletter.col.langue": "Language",
    "admin.newsletter.col.date": "Subscription date",
    "admin.newsletter.filter.active": "Active",
    "admin.newsletter.filter.inactive": "Unsubscribed",
    "admin.newsletter.unsubscribe": "Unsubscribe",
    "admin.newsletter.empty": "No subscribers.",
    "admin.newsletter.total": "{count} subscribers",
  },
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("fr");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("ramba.locale") as Locale | null;
    if (saved && LOCALES.includes(saved)) setLocaleState(saved);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    if (typeof window !== "undefined") window.localStorage.setItem("ramba.locale", l);
  };

  const t = (key: string, vars?: Record<string, string | number>) => {
    let str = T[locale][key] ?? T.fr[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.replace(`{${k}}`, String(v));
      }
    }
    return str;
  };

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

// Low-data mode hook
export function useLowData() {
  const [lowData, setLowData] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("ramba.lowData") === "1";
    setLowData(saved);
    document.documentElement.classList.toggle("low-data", saved);
  }, []);
  const toggle = () => {
    const next = !lowData;
    setLowData(next);
    window.localStorage.setItem("ramba.lowData", next ? "1" : "0");
    document.documentElement.classList.toggle("low-data", next);
  };
  return { lowData, toggle };
}
